import { MITRE_TACTICS } from '@minisoc/contracts';
import {
  CHART_COLORS,
  Card,
  CardHeader,
  EmptyState,
  FilterTabs,
  PageHeader,
  StatCard,
  cn,
  formatMinutes,
  formatPercent,
} from '@minisoc/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../api';

const RANGES = [
  { value: '30', label: '30 jours' },
  { value: '90', label: '90 jours' },
] as const;

export function KpiPage() {
  const [range, setRange] = useState<'30' | '90'>('30');
  const kpi = useQuery({
    queryKey: ['kpi', 'dashboard', range],
    queryFn: () => api.getKpiDashboard(Number(range)),
  });

  const data = kpi.data;

  return (
    <>
      <PageHeader
        title="KPI / Analytics"
        subtitle="Exposition réelle de votre parc, exprimée dans le référentiel MITRE ATT&CK"
        action={<FilterTabs options={RANGES} value={range} onChange={setRange} />}
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Alertes ouvertes" value={data?.counters.open ?? '—'} tone="text-critical" />
        <StatCard
          label={`Résolues (${range}j)`}
          value={data?.counters.resolvedInRange ?? '—'}
          tone="text-success"
        />
        <StatCard
          label="MTTR moyen"
          value={data ? formatMinutes(data.mttrMinutes) : '—'}
          tone="text-brand-400"
          hint="Détection → clôture"
        />
        <StatCard
          label="Taux de faux positifs"
          value={data ? formatPercent(data.counters.falsePositiveRate, 0) : '—'}
          tone="text-high"
          hint="Qualité de la détection"
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="xl:col-span-2">
          <CardHeader
            title="Évolution des alertes"
            subtitle={`Alertes détectées par jour sur ${range} jours`}
          />
          <div className="h-64 px-2 py-4">
            <ResponsiveContainer width="100%" height="100%">
              {/* Recharts attend un tableau mutable ; les contrats de l'API sont en lecture seule. */}
              <AreaChart data={[...(data?.timeline ?? [])]}>
                <defs>
                  <linearGradient id="grad-critical" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS.critical} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={CHART_COLORS.critical} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="grad-high" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS.high} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={CHART_COLORS.high} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={CHART_COLORS.grid} vertical={false} />
                <XAxis
                  dataKey="date"
                  stroke={CHART_COLORS.axis}
                  fontSize={11}
                  // Une étiquette par jour serait illisible sur 90 jours ; on
                  // n'affiche que le jour et le mois, espacés automatiquement.
                  tickFormatter={(value: string) => value.slice(5).replace('-', '/')}
                  minTickGap={24}
                />
                <YAxis stroke={CHART_COLORS.axis} fontSize={11} allowDecimals={false} width={28} />
                <Tooltip
                  contentStyle={{
                    background: '#0c1220',
                    border: '1px solid #1e2a45',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelStyle={{ color: '#8698ba' }}
                />
                <Area
                  type="monotone"
                  dataKey="critical"
                  name="Critiques"
                  stroke={CHART_COLORS.critical}
                  fill="url(#grad-critical)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="high"
                  name="Élevées"
                  stroke={CHART_COLORS.high}
                  fill="url(#grad-high)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <CardHeader title="Règles les plus déclenchées" />
          <div className="h-64 px-2 py-4">
            {data && data.topRules.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[...data.topRules]} layout="vertical">
                  <CartesianGrid stroke={CHART_COLORS.grid} horizontal={false} />
                  <XAxis type="number" stroke={CHART_COLORS.axis} fontSize={11} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="ruleCode"
                    stroke={CHART_COLORS.axis}
                    fontSize={11}
                    width={64}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#0c1220',
                      border: '1px solid #1e2a45',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(value: number, _name, item) => [
                      `${value} alerte(s)`,
                      (item.payload as { name: string }).name,
                    ]}
                  />
                  <Bar dataKey="count" name="Alertes" fill={CHART_COLORS.brand} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState title="Aucune alerte sur la période" />
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="Machines et comptes les plus ciblés" />
          <div className="grid gap-6 p-5 sm:grid-cols-2">
            <TopList
              title="Machines"
              rows={data?.topEndpoints.map((row) => ({ label: row.endpoint, count: row.count })) ?? []}
            />
            <TopList
              title="Comptes"
              rows={data?.topUsers.map((row) => ({ label: row.user, count: row.count })) ?? []}
            />
          </div>
        </Card>

        <Card className="xl:col-span-2">
          <CardHeader
            title="Couverture MITRE ATT&CK"
            subtitle="Techniques réellement observées sur votre parc. Partageable avec un auditeur ou un assureur cyber."
          />
          <MitreHeatmap coverage={data?.mitreCoverage ?? []} />
        </Card>
      </div>
    </>
  );
}

function TopList({ title, rows }: { title: string; rows: { label: string; count: number }[] }) {
  const max = Math.max(1, ...rows.map((row) => row.count));

  return (
    <div>
      <p className="label-caps">{title}</p>
      <ul className="mt-2.5 space-y-2">
        {rows.length === 0 ? <li className="text-sm text-text-dim">Aucune donnée</li> : null}
        {rows.map((row) => (
          <li key={row.label}>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="truncate font-mono text-text">{row.label}</span>
              <span className="shrink-0 font-mono text-text-muted">{row.count}</span>
            </div>
            <div className="mt-1 h-1 overflow-hidden rounded-full bg-border-subtle">
              <div
                className="h-full rounded-full bg-brand-500"
                style={{ width: `${(row.count / max) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Heatmap tactique × technique.
 *
 * Seules les tactiques réellement observées sont affichées : présenter les
 * douze colonnes du référentiel donnerait l'impression d'une couverture de
 * détection que MiniSOC ne revendique pas au stade du MVP (7 règles Red Flag).
 */
function MitreHeatmap({
  coverage,
}: {
  coverage: readonly {
    tacticId: string;
    tacticName: string;
    techniqueId: string;
    techniqueName: string;
    count: number;
  }[];
}) {
  if (coverage.length === 0) {
    return (
      <EmptyState
        title="Aucune technique observée"
        description="Aucune alerte sur la période, donc aucune technique d'attaque constatée sur votre parc."
      />
    );
  }

  const max = Math.max(...coverage.map((entry) => entry.count));
  const tactics = MITRE_TACTICS.filter((tactic) =>
    coverage.some((entry) => entry.tacticId === tactic.id),
  );

  return (
    <div className="overflow-x-auto p-5">
      <div className="flex gap-4">
        {tactics.map((tactic) => {
          const entries = coverage.filter((entry) => entry.tacticId === tactic.id);

          return (
            <div key={tactic.id} className="min-w-44 flex-1">
              <p className="border-b border-border-subtle pb-2 text-xs font-semibold text-text">
                {tactic.nameFr}
                <span className="mt-0.5 block font-mono text-[0.625rem] font-normal text-text-dim">
                  {tactic.id}
                </span>
              </p>

              <ul className="mt-2 space-y-1.5">
                {entries.map((entry) => {
                  const intensity = entry.count / max;

                  return (
                    <li
                      key={entry.techniqueId}
                      title={`${entry.techniqueId} — ${entry.techniqueName} : ${entry.count} alerte(s)`}
                      className={cn(
                        'rounded-lg px-2.5 py-2 text-xs ring-1',
                        intensity > 0.66
                          ? 'bg-critical/20 text-text ring-critical/40'
                          : intensity > 0.33
                            ? 'bg-high/15 text-text ring-high/30'
                            : 'bg-electric/10 text-text-muted ring-electric/25',
                      )}
                    >
                      <span className="font-mono">{entry.techniqueId}</span>
                      <span className="float-right font-mono font-bold">{entry.count}</span>
                      <span className="mt-0.5 block truncate text-[0.6875rem] text-text-dim">
                        {entry.techniqueName}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
